import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { enGB, nl } from 'date-fns/locale';
import { TbEdit, TbTrash } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import communicationsService from '../../../services/community/communicationsService';
import useCommunications from '../../../hooks/community/useCommunications';
import {
  CommunicationType,
  PostcardOrder,
  ScheduledEmail,
  ScheduledNotification,
} from '../../../types/communication';
import CommunicationBadge from '../badges/CommunicationBadge';
import { capitalizeFirstLetter, formatDateAndTime } from '../../../utils/stringUtils';
import Avatar from '../misc/Avatar';
import WarningModal from '../modals/WarningModal';
import useLanguage from '../../../hooks/account/useLanguage';
import Language from '../../../types/language';
import { ActionMenu, ActionMenuButton } from '../menus/ActionMenu';
import SearchBar from '../forms/SearchBar';
import { usePagination } from '../../../hooks/effects/usePagination';
import DatePicker from '../forms/DatePicker';
import dateUtils from '../../../utils/dateUtils';
import { withinDateBounds } from '../../../utils/validationUtils';
import connectionService from '../../../services/community/connectionService';

export default function CommunicationsTable() {
  const { t } = useTranslation();
  const { emails, notifications, postcards } = useCommunications() ?? {};
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(
    dateUtils.setHoursToZero(new Date()),
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    dateUtils.setHoursToZero(new Date(Date.now() + 1000 * 60 * 60 * 24 * 30 * 3)),
  ); // 3 months

  const communications = [...(emails ?? []), ...(notifications ?? []), ...(postcards ?? [])].sort(
    (a, b) => new Date(a.sendDate).getTime() - new Date(b.sendDate).getTime(),
  );

  const filteredCommunications = communications.filter((c) => {
    if (!withinDateBounds(c, startDate, endDate)) return false;

    if (search === '') return true;
    if ('emailId' in c) {
      return (
        c.body.toLowerCase().includes(search.toLowerCase()) ||
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        formatDateAndTime(new Date(c.sendDate)).toLowerCase().includes(search.toLowerCase())
      );
    }

    return (
      c.message.toLowerCase().includes(search.toLowerCase()) ||
      formatDateAndTime(new Date(c.sendDate)).toLowerCase().includes(search.toLowerCase())
    );
  });

  const pagination = usePagination(filteredCommunications);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchBar
          inputProps={{ placeholder: t('page.communicationOverview.search') }}
          search={search}
          setSearch={setSearch}
          className="md:w-80 lg:w-96"
        />
        <div className="flex items-center gap-1 rounded-[8px] bg-secondary-200/70 px-2 py-1">
          <DatePicker
            customFormat="D MMM YYYY"
            date={startDate}
            setDate={setStartDate}
            includeTime={false}
          />
          <span className="px-1 text-gray-400">–</span>
          <DatePicker
            customFormat="D MMM YYYY"
            date={endDate}
            setDate={setEndDate}
            includeTime={false}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {pagination.items.map((c, i) => (
          <CommunicationTile key={i} communication={c} />
        ))}
      </div>

      {pagination.numberOfPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => pagination.setPage(pagination.page - 1)}
            className="rounded-[8px] px-2 py-1 hover:bg-secondary-200 disabled:opacity-40">
            ‹
          </button>
          <span>
            {t('component.pagination.pageOf', {
              defaultValue: 'Page {{page}} of {{total}}',
              page: pagination.page,
              total: pagination.numberOfPages,
            })}
          </span>
          <button
            type="button"
            disabled={pagination.page >= pagination.numberOfPages}
            onClick={() => pagination.setPage(pagination.page + 1)}
            className="rounded-[8px] px-2 py-1 hover:bg-secondary-200 disabled:opacity-40">
            ›
          </button>
        </div>
      )}
    </div>
  );
}

interface CommunicationTileProps {
  communication: ScheduledEmail | ScheduledNotification | PostcardOrder;
}

function CommunicationTile({ communication }: CommunicationTileProps) {
  const [pictures, setPictures] = useState<string[]>([]);
  const { t } = useTranslation();
  const lang = useLanguage();
  const locale = lang === Language.NL ? nl : enGB;

  const type = getType(communication);

  const title =
    type === CommunicationType.EMAIL
      ? (communication as ScheduledEmail).title || (communication as ScheduledEmail).body
      : (communication as ScheduledNotification | PostcardOrder).message;

  useEffect(() => {
    const fetchPictures = async () => {
      const ps = await Promise.all(
        communication.dashboardMembersIds.map((dashboardMemberId) =>
          connectionService.getProfilePictureUrl(dashboardMemberId).catch(() => ''),
        ),
      );
      setPictures(ps.filter(Boolean).splice(0, 3));
    };

    fetchPictures();
  }, []);

  const numInvited = communication.dashboardMembersIds.length;
  const isSent =
    'isSent' in communication ? communication.isSent : communication.sendDate < new Date();
  const sendDate =
    type === CommunicationType.POSTCARD
      ? addDays(new Date(communication.sendDate), 1)
      : communication.sendDate;

  return (
    <div className="flex w-full flex-wrap items-center gap-3 rounded-[8px] bg-secondary-200/70 px-3 py-3 transition-colors hover:bg-secondary-200 sm:flex-nowrap sm:gap-4">
      <CommunicationBadge type={type} className="h-10 w-10 flex-shrink-0 rounded-[8px]" />

      <div className="min-w-0 flex-1 basis-[40%]">
        <p className="truncate font-semibold text-primary-900">{title}</p>
        <p className="text-sm text-gray-500">{capitalizeFirstLetter(type)}</p>
      </div>

      <p className="w-full flex-shrink-0 text-sm font-medium text-primary-900 sm:w-36 sm:text-center">
        {format(sendDate, `dd-MM-yyyy${type !== CommunicationType.POSTCARD ? ' HH:mm' : ''}`, {
          locale,
        })}
      </p>

      <div className="flex min-w-[7rem] flex-shrink-0 items-center">
        {pictures.map((p, i) => (
          <Avatar
            key={i}
            className={`h-8 w-8 rounded-full ${i > 0 ? '-ml-2' : ''}`}
            alias=""
            src={p}
          />
        ))}
        <p className="ml-2 text-sm text-gray-600">
          {numInvited}{' '}
          {t('page.communicationOverview.table.receivers', {
            defaultValue: t('page.communicationOverview.table.invited'),
          })}
        </p>
      </div>

      <div
        className={classNames(
          'inline-flex h-fit w-fit flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
          {
            'bg-green-100 text-green-800': isSent,
            'bg-primary-100 text-primary-900': !isSent,
          },
        )}>
        <span
          className={classNames('h-1.5 w-1.5 rounded-full', {
            'bg-green-600': isSent,
            'bg-primary': !isSent,
          })}
        />
        {t(`page.communicationOverview.table.${isSent ? 'sent' : 'scheduled'}`)}
      </div>

      <div className="ml-auto flex-shrink-0 sm:ml-0">
        <CommunicationOptions communication={communication} />
      </div>
    </div>
  );
}

interface CommunicationOptionsProps {
  communication: ScheduledEmail | ScheduledNotification | PostcardOrder;
}

function CommunicationOptions({ communication }: CommunicationOptionsProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSent =
    'isSent' in communication ? communication.isSent : communication.sendDate < new Date();

  const handleEdit = () =>
    navigate('/communication/edit', {
      state: {
        communication,
        type: getType(communication),
      },
    });

  const handleDelete = () => {
    if ((communication as ScheduledEmail).emailId) {
      communicationsService.deleteScheduledEmail((communication as ScheduledEmail).emailId);
    } else
      communicationsService.deleteScheduledNotification(
        (communication as ScheduledNotification).notificationId,
      );
  };

  return (
    <>
      <ActionMenu>
        {!isSent && (
          <ActionMenuButton
            icon={TbEdit}
            text={t(`page.communicationOverview.table.edit`)}
            onClick={handleEdit}
          />
        )}
        {!(communication as PostcardOrder).orderId && (
          <ActionMenuButton
            className="text-danger"
            icon={TbTrash}
            text={t(`page.communicationOverview.table.delete`)}
            onClick={() => setDeleteModalOpen(true)}
          />
        )}
      </ActionMenu>
      <WarningModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title={t(`page.communicationOverview.table.deleteModal.title`)}
        description={t(`page.communicationOverview.table.deleteModal.description`)}
        onContinue={handleDelete}
        saveClassName="bg-danger text-secondary-50"
        button={t(`page.communicationOverview.table.delete`)}
      />
    </>
  );
}

function getType(communication: ScheduledEmail | ScheduledNotification | PostcardOrder) {
  if ('emailId' in communication) return CommunicationType.EMAIL;
  if ('notificationId' in communication) return CommunicationType.NOTIFICATION;
  return CommunicationType.POSTCARD;
}
